<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { isCoraContext } from '@sancare/typescript-commons/src/misc/cora'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { useStore } from '@/store'
import { ModeEnum, PmsiTypeEnum } from '@/store/modules/settings/types'
import { SmrStay } from '@/store/modules/stay/smr/types'
import { Stay, StayStatusEnum } from '@/store/modules/stay/types'

export interface StayValidatorProps {
  stay: Stay | SmrStay
  automationGroupId?: number | null
}

const store = useStore()
const { t } = useI18n()

const props = defineProps<StayValidatorProps>()

const isFetching = ref(false)

const inCoraContext = isCoraContext()

const updateStayStatusRequest = computed(() => pmsiType.value === PmsiTypeEnum.MCO ? store.state.stayDisplayer.updateStayStatusRequest : store.state.smrStay.requests.updateStayStatus)
const buttonLabel = computed(() => {
  if (props.stay.status === 'validated') {
    return t('stayDisplayer.validator.unvalidate')
  }
  return inCoraContext ? t('stayDisplayer.validator.validateCora') : t('stayDisplayer.validator.validate')
})
const validateTooltip = computed(() => {
  if (props.stay.status === StayStatusEnum.VALIDATED) {
    return null
  }
  return inCoraContext ? t('stayDisplayer.validator.tooltipCora') : t('stayDisplayer.validator.tooltip')
})
const validationStateClass = computed(() => `btn-grouping-validation__${props.stay.status === StayStatusEnum.VALIDATED ? 'validated' : 'unvalidated'}`)
const pmsiType = computed<PmsiTypeEnum>(() => store.state.settings.pmsi.pmsiType)
const mode = computed<ModeEnum>(() => store.state.settings.pmsi.mode)
const actionName = computed(() => pmsiType.value === PmsiTypeEnum.MCO ? 'updateStayStatus' : 'smrStay/updateStayStatus')
async function validateStay() {
  if (updateStayStatusRequest.value.fetching) {
    return
  }
  isFetching.value = true
  await store.dispatch(
    actionName.value,
    {
      stayId: props.stay.id,
      status: 'validated',
      mode:  mode.value,
      pmsiType: pmsiType.value,
      automationGroupId: props.automationGroupId,
    }
  )
  isFetching.value = false
}
async function unvalidateStay() {
  if (updateStayStatusRequest.value.fetching) {
    return
  }
  isFetching.value = true
  await store.dispatch(
    actionName.value,
    {
      stayId: props.stay.id,
      status: 'unvalidated',
      mode: mode.value,
      pmsiType: pmsiType.value
    }
  )
  isFetching.value = false
}
</script>

<template>
  <div
    v-tooltip.bottom="validateTooltip"
    :class="{
      'loading': isFetching,
      [validationStateClass]: true
    }"
    class="btn btn-grouping-validation"
    @click="stay.status === 'validated' ? unvalidateStay() : validateStay()"
  >
    <div
      class="btn__label"
      :class="{ 'btn-loading__label': isFetching }"
    >
      {{ buttonLabel }}
    </div>
    <div
      v-if="stay.status !== StayStatusEnum.VALIDATED || isFetching"
      :class="isFetching ? 'btn-loading__icon' : 'btn__icon'"
    >
      <sancare-octicon
        v-if="!isFetching"
        name="arrow-right"
      />
      <div
        v-else
        class="loader loader-xs loader-purple btn-loading"
      />
    </div>
  </div>
</template>

